import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useSearchParam } from "#/shared/hooks";
import { PYTHON_SUPPORT_MODAL_ID } from "#/shared/ui/organisms/PythonSupportModal";
import { useAppDispatch } from "#/store/hooks";
import type { SerializedPythonArg } from "../lib/createPythonRuntimeArgs";
import { pythonRunner } from "../lib/pythonRunner";
import { pyodideSlice } from "../model/pyodideSlice";
import type { ExecutionResult } from "./useCodeExecution";
import { usePyodideProgressSnackbar } from "./usePyodideProgressSnackbar";
const PYTHON_EXEC_MODE = process.env.NEXT_PUBLIC_PYTHON_EXEC_MODE ?? "pyodide";

/** Delay after 100% so the progress bar animation completes before snackbar closes. */
const PROGRESS_COMPLETE_DELAY_MS = 400;
export const usePythonCodeRunner = () => {
  const $ = _c(12);
  const dispatch = useAppDispatch();
  const [, setModalName] = useSearchParam("modal");
  usePyodideProgressSnackbar();
  let t0;
  if ($[0] !== setModalName) {
    t0 = () => {
      setModalName(PYTHON_SUPPORT_MODAL_ID);
    };
    $[0] = setModalName;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const openPythonSupportModal = t0;
  let t1;
  let t2;
  if ($[2] !== dispatch) {
    t1 = () => {
      if (PYTHON_EXEC_MODE !== "pyodide") {
        return;
      }
      if (pythonRunner.isReady) {
        return;
      }
      let cancelled = false;
      dispatch(pyodideSlice.actions.setProgress({
        value: 0,
        stage: "Starting\u2026"
      }));
      let completeTimeoutId = null;
      pythonRunner.init({
        onProgress: (value, stage) => {
          if (!cancelled) {
            dispatch(pyodideSlice.actions.setProgress({
              value,
              stage
            }));
          }
        }
      }).finally(() => {
        if (cancelled) {
          return;
        }
        completeTimeoutId = setTimeout(() => {
          completeTimeoutId = null;
          if (!cancelled) {
            dispatch(pyodideSlice.actions.clearProgress());
          }
        }, PROGRESS_COMPLETE_DELAY_MS);
      });
      return () => {
        cancelled = true;
        if (completeTimeoutId !== null) {
          clearTimeout(completeTimeoutId);
        }
        dispatch(pyodideSlice.actions.clearProgress());
      };
    };
    t2 = [dispatch];
    $[2] = dispatch;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== openPythonSupportModal) {
    t3 = {
      mutationFn: _temp,
      onError: () => {
        if (PYTHON_EXEC_MODE !== "pyodide") {
          openPythonSupportModal();
        }
      }
    };
    $[5] = openPythonSupportModal;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    mutateAsync: executePythonCode,
    isPending
  } = useMutation(t3);
  let t4;
  if ($[7] !== executePythonCode) {
    t4 = async (codeInput_0, args_0) => await executePythonCode({
      codeInput: codeInput_0,
      args: args_0
    });
    $[7] = executePythonCode;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const runPythonCode = t4;
  let t5;
  if ($[9] !== isPending || $[10] !== runPythonCode) {
    t5 = {
      runPythonCode,
      isProcessing: isPending
    };
    $[9] = isPending;
    $[10] = runPythonCode;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
async function _temp(t0) {
  const {
    codeInput,
    args
  } = t0;
  if (PYTHON_EXEC_MODE === "pyodide") {
    return pythonRunner.run(codeInput, undefined, args);
  }
  const response = await fetch("http://localhost:8085/python", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      code: codeInput,
      args
    })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const result = await response.json();
  if (!result) {
    throw new Error("No result returned from Python execution");
  }
  return result as ExecutionResult;
}